/* hook-board-configurator.js — sibling of configurator.js + corner-configurator.js,
 * scoped to hook boards.
 *
 * Init pattern matches slab: defer to DOMContentLoaded /
 * shopify:section:load so the IIFE runs AFTER the inline config script and
 * the <hook-board-configurator> element have been parsed (script_tag emits
 * a non-deferred script tag).
 *
 * URL state keys hs/hf so slab + corner + hook-board coexist in one URL.
 */

'use strict';

(function () {
  if (window.__hookBoardConfiguratorLoaded) return;
  window.__hookBoardConfiguratorLoaded = true;

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
  if (document.readyState !== 'loading') initAll();

  function initAll() {
    document.querySelectorAll('hook-board-configurator').forEach(initOne);
  }

  function initOne(root) {
    if (root.__hbdInitialized) return;
    root.__hbdInitialized = true;
    try { initRoot(root); }
    catch (err) { console.error('[hook-board-configurator] init failed:', err); }
  }

  function initRoot(root) {
    const cfg = window.HOOK_BOARD_CONFIG;
    if (!cfg) { console.error('[hook-board-configurator] HOOK_BOARD_CONFIG missing'); return; }

    function currentMaterial() {
      if (window.SLAB_CONFIG && window.SLAB_CONFIG.currentMaterial) {
        return window.SLAB_CONFIG.currentMaterial;
      }
      return cfg.currentMaterial;
    }

    const $form      = root.querySelector('form');
    const $sizeSel   = root.querySelector('[data-hbd="size"]');
    const $sizeList  = root.querySelector('[data-hbd="size-list"]');
    const $finList   = root.querySelector('[data-hbd="finish-list"]');
    const $colorList = root.querySelector('[data-hbd="color-list"]');
    const $price     = root.querySelector('[data-hbd="price"]');
    const $status    = root.querySelector('[data-hbd="status"]');
    const $variant   = root.querySelector('[data-hbd="variant-id"]');
    const $atc       = root.querySelector('[data-hbd="atc"]');

    if (!$form || !$sizeSel || !$finList || !$colorList) {
      console.error('[hook-board-configurator] required elements missing');
      return;
    }

    // Hook color catalog (Billy 2026-06-08): 4 options, all same price,
    // line-item property only — no pricing or variant impact. Black Matte
    // is the default selection.
    const HOOK_COLORS = [
      { id: 'black-matte',    label: 'Black Matte',    isDefault: true },
      { id: 'brushed-nickel', label: 'Brushed Nickel'                  },
      { id: 'chrome',         label: 'Chrome'                          },
      { id: 'satin-gold',     label: 'Satin Gold'                      },
    ];

    // ── State ────────────────────────────────────────────────────────
    const state = {
      size:       (cfg.sizes[0] || {}).code || '',
      finish:     'finished',
      finishChip: 'clear-matte',
      hookColor:  (HOOK_COLORS.find(c => c.isDefault) || HOOK_COLORS[0]).id,
    };

    // ── Finish rebuild ───────────────────────────────────────────────
    // Mirrors slab's checkbox-chip list; the change handler enforces
    // single-select semantics across the markup-level checkboxes.
    function rebuildFinishOptions() {
      const mat = currentMaterial();

      // Locked single-chip materials (Painted White, Primed, U-Paint MDF):
      // collapse the finish selector to one disabled chip so Matte/Satin/Rubio
      // never show. The chip label rides to the cart; state.finish (the API
      // value) stays as configured. Mirrors slab's materialFinishOverrides.
      const overrides = cfg.materialFinishOverrides || {};
      const override  = mat ? overrides[mat.name] : null;
      if (override) {
        state.finishChip = override.id;
        state.finish     = override.finish;
        $atc.disabled = false;
        $finList.innerHTML =
          '<label class="hbd-chip hbd-chip--locked" data-hbd-finish="' + esc(override.id) + '" data-finish="' + esc(override.finish) + '">'
            + '<span>' + esc(override.label) + '</span>'
            + '<input type="checkbox" data-hbd-finish-input value="' + esc(override.id) + '" checked disabled>'
          + '</label>';
        return;
      }

      const tier = mat ? String(mat.tier) : null;
      const allowed = (tier && cfg.materialFinishes[tier]) || [];
      const rubioOk = !!(mat && mat.rubioAllowed);

      const filtered = cfg.finishOptions.filter(opt => {
        if (opt.requiresRubioAllowed) return rubioOk && allowed.includes('finished');
        return allowed.includes(opt.finish);
      });

      if (!filtered.length) {
        $finList.innerHTML = '<span class="hbd-chip hbd-chip--unavailable"><span>Not available</span></span>';
        state.finish     = '';
        state.finishChip = '';
        $atc.disabled = true;
        return;
      }
      $atc.disabled = false;

      const allowedIds = filtered.map(o => o.id);
      if (!allowedIds.includes(state.finishChip)) {
        const def = filtered.find(o => o.isDefault) || filtered[0];
        state.finishChip = def.id;
        state.finish     = def.finish;
      }

      $finList.innerHTML = filtered.map(opt => {
        const checked = (opt.id === state.finishChip) ? ' checked' : '';
        return (
          '<label class="hbd-chip" data-hbd-finish="' + esc(opt.id) + '" data-finish="' + esc(opt.finish) + '">'
            + '<span>' + esc(opt.label) + '</span>'
            + '<input type="checkbox" data-hbd-finish-input value="' + esc(opt.id) + '"' + checked + '>'
          + '</label>'
        );
      }).join('');
    }

    function esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({
        '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;',
      })[c]);
    }

    // Resolve a finish chip id to its customer-facing label, checking the
    // materialFinishOverrides (Painted/Primed/etc.) as well as the standard
    // finishOptions catalog so locked override chips label correctly on the cart.
    function finishLabel(chipId) {
      const opt = cfg.finishOptions.find(o => o.id === chipId);
      if (opt) return opt.label;
      const overrides = cfg.materialFinishOverrides || {};
      for (const k in overrides) {
        if (overrides[k].id === chipId) return overrides[k].label;
      }
      return chipId;
    }

    // ── URL state ────────────────────────────────────────────────────
    const URL_KEYS = { size: 'hs', finishChip: 'hf', hookColor: 'hc' };

    function readStateFromURL() {
      const p = new URLSearchParams(location.search);
      const hs = p.get(URL_KEYS.size);
      const hf = p.get(URL_KEYS.finishChip);
      const hc = p.get(URL_KEYS.hookColor);
      if (hs && cfg.sizes.some(s => s.code === hs)) state.size = hs;
      if (hf) {
        const opt = cfg.finishOptions.find(o => o.id === hf);
        if (opt) { state.finishChip = opt.id; state.finish = opt.finish; }
      }
      if (hc && HOOK_COLORS.some(c => c.id === hc)) state.hookColor = hc;
    }

    function pushStateToURL() {
      const p = new URLSearchParams(location.search);
      p.set(URL_KEYS.size,       state.size);
      p.set(URL_KEYS.finishChip, state.finishChip);
      p.set(URL_KEYS.hookColor,  state.hookColor);
      history.replaceState(null, '', location.pathname + '?' + p.toString());
    }

    // ── Pricing ──────────────────────────────────────────────────────
    let priceSeq = 0;
    let priceTimer = null;

    function schedulePrice() {
      clearTimeout(priceTimer);
      priceTimer = setTimeout(fetchPrice, 200);
    }

    async function fetchPrice() {
      const mat = currentMaterial();
      if (!mat) {
        $price.textContent = '—';
        $status.textContent = 'Material not configured for this page.';
        return;
      }
      if (!state.finish) {
        $price.textContent = '—';
        $status.textContent = '';
        return;
      }

      const mySeq = ++priceSeq;
      const params = new URLSearchParams({
        subtype:       'hook_board',
        product_id:    cfg.productId,
        // Match slab convention. The hook_board_pricing table stores the
        // tier number (CSV labels like "material type 3" were normalized
        // at import) — the service strips the prefix again at lookup so
        // both formats resolve.
        material:      'material type ' + mat.tier,
        material_name: mat.name,
        finish:        state.finish,
        size:          state.size,
      });

      try {
        const res = await fetch(cfg.apiUrl + '/api/price?' + params.toString(), {
          headers: { 'Accept': 'application/json' },
        });
        if (mySeq !== priceSeq) return;
        const data = await res.json().catch(() => null);
        if (!res.ok || !data) {
          $price.textContent = '—';
          $status.textContent = (data && data.error) ? errorMessage(data.error) : 'Price unavailable.';
          return;
        }
        $price.textContent = formatPrice(data.price);
        $status.textContent = '';
      } catch (err) {
        if (mySeq !== priceSeq) return;
        $price.textContent = '—';
        $status.textContent = 'Network error.';
      }
    }

    function errorMessage(code) {
      switch (code) {
        case 'missing_params':         return 'Please choose size and finish.';
        case 'not_found':              return 'No price for this combination.';
        case 'finish_not_allowed':     return 'This finish is not available for the selected material.';
        case 'material_not_eligible':  return 'This material is not Rubio-eligible.';
        default:                       return 'Price unavailable.';
      }
    }

    function formatPrice(n) {
      return '$' + Number(n).toFixed(2);
    }

    // ── Add to cart (mirrors slab's AJAX flow) ───────────────────────
    const ATC_SYNC_GAP_SECONDS    = 5;
    const ATC_REASSURE_INTERVAL_MS = 1500;
    const ATC_CART_ADD_BACKOFFS_MS = [500, 1500, 3000];
    const ATC_REASSURE_MSGS = [
      'Almost there…',
      'Syncing with Shopify…',
      'Just a moment more…',
      'Finalizing your order…',
      'Hang tight…',
      'Wrapping up…',
    ];
    const ATC_DEFAULT_LABEL = $atc.textContent || 'Add Hook Board to Cart';

    $form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const mat = currentMaterial();
      if (!mat || !state.finish) { $status.textContent = 'Not configured.'; return; }

      setAtcBusy(true);
      setAtcLabel('Adding…');
      $status.textContent = '';

      try {
        const cfgRes = await fetch(cfg.apiUrl + '/api/configure', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            subtype:       'hook_board',
            product_id:    cfg.productId,
            material:      'material type ' + mat.tier,
            material_name: mat.name,
            finish:        state.finish,
            size:          state.size,
          }),
        });
        const data = await cfgRes.json().catch(() => ({}));
        if (!cfgRes.ok || !data.variant_id) {
          throw new Error(data.error || 'configure_failed');
        }

        $variant.value = data.variant_id;
        appendLineProps(data.price, mat);

        await runAtcCountdown(ATC_SYNC_GAP_SECONDS);

        startAtcReassurance();
        try { await focalCartAdd($form); }
        finally { stopAtcReassurance(); }
      } catch (err) {
        $status.textContent = friendlyAtcError(err);
      } finally {
        stopAtcReassurance();
        stopAtcCountdown();
        setAtcBusy(false);
        setAtcLabel(ATC_DEFAULT_LABEL);
      }
    }, /*useCapture*/ true);

    function setAtcBusy(busy) {
      $atc.disabled = !!busy;
      if (busy) $atc.setAttribute('aria-busy', 'true');
      else      $atc.removeAttribute('aria-busy');
    }
    function setAtcLabel(text) { $atc.textContent = text; }

    let __hbdCountdownTimer = null;
    function runAtcCountdown(seconds) {
      return new Promise((resolve) => {
        stopAtcCountdown();
        let n = seconds;
        $atc.textContent = 'Preparing… ' + n + 's';
        __hbdCountdownTimer = setInterval(() => {
          n -= 1;
          if (n <= 0) { stopAtcCountdown(); resolve(); return; }
          $atc.textContent = 'Preparing… ' + n + 's';
        }, 1000);
      });
    }
    function stopAtcCountdown() {
      if (__hbdCountdownTimer) { clearInterval(__hbdCountdownTimer); __hbdCountdownTimer = null; }
    }

    let __hbdReassureTimer = null;
    function startAtcReassurance() {
      stopAtcReassurance();
      let lastIdx = -1;
      const pick = () => {
        let i;
        do { i = Math.floor(Math.random() * ATC_REASSURE_MSGS.length); }
        while (i === lastIdx && ATC_REASSURE_MSGS.length > 1);
        lastIdx = i;
        $atc.textContent = ATC_REASSURE_MSGS[i];
      };
      pick();
      __hbdReassureTimer = setInterval(pick, ATC_REASSURE_INTERVAL_MS);
    }
    function stopAtcReassurance() {
      if (__hbdReassureTimer) { clearInterval(__hbdReassureTimer); __hbdReassureTimer = null; }
    }

    async function focalCartAdd(form) {
      const variantInput = form.querySelector('input[name="id"]');
      const variantId = variantInput ? variantInput.value : '';
      const sectionsToBundle = ['variant-added'];

      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      }));

      const formData = new FormData(form);
      formData.set('sections', sectionsToBundle.join(','));
      formData.set('sections_url', shopifyRoot() + 'variants/' + variantId);

      const addJson = await postCartAddWithBackoff(formData);

      const cartContent = await fetch(shopifyRoot() + 'cart.js', {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      }).then(r => r.json());
      cartContent.sections = addJson.sections;

      form.dispatchEvent(new CustomEvent('variant:add', {
        bubbles: true,
        detail: { items: addJson.items || [addJson], cart: cartContent },
      }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', cart: cartContent },
      }));
    }

    async function postCartAddWithBackoff(formData) {
      const attempts = ATC_CART_ADD_BACKOFFS_MS.length + 1;
      let lastErr = null;
      for (let i = 0; i < attempts; i++) {
        const res = await fetch(shopifyRoot() + 'cart/add.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        if (res.ok) return res.json();
        const body = await res.json().catch(() => ({}));
        const err = new Error(body.description || body.message || 'cart_add_' + res.status);
        err.status = res.status; err.body = body;
        lastErr = err;
        if (res.status === 422 && i < attempts - 1) {
          await new Promise(r => setTimeout(r, ATC_CART_ADD_BACKOFFS_MS[i]));
          continue;
        }
        throw err;
      }
      throw lastErr;
    }

    function shopifyRoot() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }

    function friendlyAtcError(err) {
      if (err && err.status === 422) {
        return 'Cart is taking longer than usual to sync. Please click Add Hook Board to Cart again.';
      }
      const m = (err && err.message) || '';
      if (m === 'configure_failed' || m === 'price_not_found') {
        return "Couldn't create variant for this combination. Please adjust your configuration.";
      }
      return errorMessage(m) || 'Could not add to cart. Please try again.';
    }

    function appendLineProps(price, mat) {
      Array.from($form.querySelectorAll('input[data-hbd-prop]')).forEach(n => n.remove());

      const sizeObj  = cfg.sizes.find(s => s.code === state.size);
      const colorObj = HOOK_COLORS.find(c => c.id === state.hookColor);

      const props = {
        'Material':   mat.name,
        'Size':       sizeObj  ? sizeObj.label  : state.size,
        'Finish':     finishLabel(state.finishChip),
        'Hook Color': colorObj ? colorObj.label : state.hookColor,
        '_price':     Number(price).toFixed(2),
      };
      Object.keys(props).forEach(k => {
        const input = document.createElement('input');
        input.type  = 'hidden';
        input.name  = 'properties[' + k + ']';
        input.value = props[k];
        input.dataset.hbdProp = '1';
        $form.appendChild(input);
      });
    }

    // Size chips (Billy 2026-06-09): rendered with the 4/5/7-hook
    // square images. Reuses the slab/corner `.cfg-build` card pattern
    // so all three configurators share one chip family. The hidden
    // <select data-hbd="size"> stays in place as the source of truth
    // for state.size — the chip change handler writes through to it.
    function rebuildSizeChips() {
      if (!$sizeList) return;
      const idPrefix = 'hbd-size-' + (cfg.sectionId || 'x');
      const images = cfg.sizeImages || {};
      $sizeList.innerHTML = (cfg.sizes || []).map(s => {
        const inputId = idPrefix + '-' + s.code;
        const checked = (s.code === state.size) ? ' checked' : '';
        const imgSrc = images[s.code] || '';
        return (
          '<label class="cfg-build" data-hbd-size="' + esc(s.code) + '" for="' + inputId + '">'
            + '<span class="cfg-build__box">'
              + (imgSrc ? '<img class="cfg-build__img" src="' + esc(imgSrc) + '" alt="' + esc(s.label) + '">' : '')
              + (imgSrc ? '<button type="button" class="cfg-build__zoom" data-xgal-zoom data-xgal-src="' + esc(imgSrc) + '" data-xgal-alt="' + esc(s.label) + '" aria-label="Zoom ' + esc(s.label) + '">'
                + '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
                  + '<circle cx="11" cy="11" r="7"></circle>'
                  + '<line x1="21" y1="21" x2="16.65" y2="16.65"></line>'
                  + '<line x1="11" y1="8" x2="11" y2="14"></line>'
                  + '<line x1="8" y1="11" x2="14" y2="11"></line>'
                + '</svg>'
              + '</button>' : '')
            + '</span>'
            + '<span class="cfg-build__check">'
              + '<input type="radio" name="hbd-size-' + esc(cfg.sectionId || 'x') + '" id="' + inputId + '" value="' + esc(s.code) + '"' + checked + '>'
              + '<span class="cfg-build__indicator"></span>'
              + '<span class="cfg-build__name">' + esc(s.label) + '</span>'
            + '</span>'
          + '</label>'
        );
      }).join('');
    }

    // ── Wiring ───────────────────────────────────────────────────────
    if ($sizeList) $sizeList.addEventListener('change', (e) => {
      const input = e.target.closest('input[type="radio"]');
      if (!input) return;
      state.size = input.value;
      // Keep the hidden <select> in sync (legacy code paths still read it).
      if ($sizeSel) $sizeSel.value = input.value;
      pushStateToURL();
      schedulePrice();
    });
    // Keep the dropdown listener too in case anything programmatically
    // sets the hidden select's value.
    $sizeSel.addEventListener('change', () => {
      state.size = $sizeSel.value;
      pushStateToURL();
      schedulePrice();
    });
    $finList.addEventListener('change', (e) => {
      const input = e.target.closest('[data-hbd-finish-input]');
      if (!input) return;
      $finList.querySelectorAll('[data-hbd-finish-input]').forEach(el => {
        el.checked = (el === input);
      });
      state.finishChip = input.value;
      const opt = cfg.finishOptions.find(o => o.id === state.finishChip);
      if (opt) state.finish = opt.finish;
      pushStateToURL();
      schedulePrice();
    });

    // Hook color: same single-select chip pattern as Finish, but the
    // choice doesn't hit /api/price (Billy: all four colors same price).
    $colorList.addEventListener('change', (e) => {
      const input = e.target.closest('[data-hbd-color-input]');
      if (!input) return;
      $colorList.querySelectorAll('[data-hbd-color-input]').forEach(el => {
        el.checked = (el === input);
      });
      state.hookColor = input.value;
      pushStateToURL();
    });

    function rebuildHookColors() {
      $colorList.innerHTML = HOOK_COLORS.map(c => {
        const checked = (c.id === state.hookColor) ? ' checked' : '';
        return (
          '<label class="hbd-chip" data-hbd-color="' + esc(c.id) + '">'
            + '<span>' + esc(c.label) + '</span>'
            + '<input type="checkbox" data-hbd-color-input value="' + esc(c.id) + '"' + checked + '>'
          + '</label>'
        );
      }).join('');
    }

    // ── Init ─────────────────────────────────────────────────────────
    readStateFromURL();
    rebuildFinishOptions();
    rebuildHookColors();
    rebuildSizeChips();
    $sizeSel.value = state.size;
    schedulePrice();
  }
})();
